'use client'

import {
  BalanceSlider,
  FlatAsset,
  OrderBoxAssetSelect,
  PaymentType,
  mapProjectToFlatAsset,
} from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import {
  useGetOtcPrice,
  useTransferUserAsset,
  useUserPortfolio,
} from '@repo/services'
import { useTransferSchema } from '@repo/shared/schemas'

import {
  ActionDetailsCondition,
  Alert,
  Button,
  Card,
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  Input,
  Row,
  Typography,
} from '@repo/ui/components'
import { useMemo, useState } from 'react'
import { TransferAmountInput } from './transfer.amount-input'
import { TransferDialog } from './transfer.dialog'

export const TransferContainer = ({
  tokenType = 'all',
}: {
  tokenType?: 'crypto' | 'property' | 'all'
}) => {
  const { t } = useTranslate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<FlatAsset | null>(null)
  const [, setSearchQuery] = useState('')
  const { data: otcPrice } = useGetOtcPrice({
    symbol: selectedAsset?.token || '',
    base: 'IRR',
    orderType: 'sell',
  })
  const availableAmount =
    +(otcPrice?.data?.targetMaxAmount ?? 0) / (otcPrice?.data?.targetScale || 1)

  const { data: userPortfolioData } = useUserPortfolio()

  // Process user portfolio data
  const projects = userPortfolioData?.data?.projects

  // Get user's assets for sell mode with project data
  const userAssets = useMemo(() => {
    const accountsData =
      userPortfolioData?.data?.accounts?.filter(
        (account) => account.type === 'project',
      ) || []

    const mappedAccounts = accountsData.map((account) => {
      // Find matching project data from projects array
      const matchingProject = projects?.find(
        (p) =>
          p.token === account.currency || p.id?.toString() === account.currency,
      )

      return {
        ...account,
        // Add project data if found
        displayName: matchingProject?.displayName || account.currency || '',
        logoURL: matchingProject?.logoURL || '',
        description: matchingProject?.description || '',
        location: matchingProject?.location || {},
        state: matchingProject?.state || '',
        tradeStatus: matchingProject?.tradeStatus || 'active',
        // Keep original account type for sorting
        accountType: account.type,
        type: matchingProject?.type || 'project',
        // Ensure token is set for API calls
        token: matchingProject?.token || account.currency || '',
        kind: matchingProject?.kind,
        scale: matchingProject?.scale || 1,
      }
    })

    // Filter out accounts with zero availableBalance and sort
    return mappedAccounts
      .filter((account) => (account.availableBalance ?? 0) > 0)
      .filter((account) => account.kind === 'property')
      .map((account) => mapProjectToFlatAsset(account))
      .sort((a, b) => {
        if (a.accountType === 'asset' && b.accountType !== 'asset') return -1
        if (a.accountType !== 'asset' && b.accountType === 'asset') return 1
        return 0
      })
  }, [userPortfolioData?.data?.accounts, projects])

  const { schema } = useTransferSchema(availableAmount)

  const { form } = useAppForm({
    formSchema: schema,
    defaultValues: {
      amount: undefined,
      range: undefined,
      transferTo: undefined,
      selectedAsset: undefined,
    },
  })

  const {
    mutate: transferUserAsset,
    data: transferUserAssetData,
    reset: transferUserAssetReset,
  } = useTransferUserAsset({
    invalidate: true,
  })

  const amount = form.watch('amount')

  const onSubmit = (data: FormSchema<typeof schema>) => {
    setDialogOpen(true)
  }

  const handleAssetChange = (asset: FlatAsset) => {
    form.setValue('selectedAsset', asset)
    form.setValue('amount', undefined)
    form.setValue('range', undefined)
    form.clearErrors()
    transferUserAssetReset()
    setSelectedAsset(asset)
  }

  const handleRangeAmountChange = (value: number) => {
    form.clearErrors('amount')
    transferUserAssetReset()
    transferUserAsset(
      {
        preview: true,
        amount: Number((value * (selectedAsset?.scale || 1)).toFixed(0)),
        currency: selectedAsset?.token,
      },
      {
        onSuccess: () => {
          form.setValue('amount', value)
        },
        onError: () => {
          form.setValue('amount', value)
        },
      },
    )
  }

  const handleAmountChange = (value: number) => {
    const currentRangeAmount = form.getValues('range')

    const newRange = (
      availableAmount > 0 ? (value / availableAmount) * 100 : 0
    ).toFixed(0)

    if (+currentRangeAmount !== +newRange) {
      transferUserAsset(
        {
          preview: true,
          amount: Number((value * (selectedAsset?.scale || 1)).toFixed(0)),
          currency: selectedAsset?.token,
        },
        {
          onSuccess: () => {
            const newRange =
              availableAmount > 0
                ? ((form.watch('amount') || 0) / availableAmount) * 100
                : 0
            form.setValue('range', newRange)
          },
          onError: () => {
            // form.setValue(
            //   'range',
            //   value > availableAmount ? availableAmount : value,
            // )
          },
        },
      )
    }
  }
  const fee = transferUserAssetData?.data?.fee

  const feeCurrency = transferUserAssetData?.data?.feeCurrency ?? 'IRR'

  const amountInIRR = transferUserAssetData?.data?.estimatedValue ?? 0

  const assetLabel =
    selectedAsset?.kind === 'crypto' ? t('common_token') : t('CM2')

  return (
    <Card className='max-w-container-md mx-auto p-xl pb-[250px]' withoutBorder>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} id='transfer-form'>
          <Col className='gap-md'>
            <Alert
              color='base'
              caption={t('transfer_process_description')}
              className='mb-md'
              icon={<Icon name='AlertTriangleLine' size='sm' />}
            />
            <FormField
              control={form.control}
              name='selectedAsset'
              render={({ field }) => (
                <FormItemWrapper inputSize='md'>
                  <OrderBoxAssetSelect
                    selectedAsset={field.value}
                    onChange={handleAssetChange}
                    error={form.formState.errors.selectedAsset?.message?.toString()}
                    projects={userAssets}
                    tab='sell'
                    isTransfer={true}
                    onSearchChange={setSearchQuery}
                    tokenType={tokenType}
                  />
                </FormItemWrapper>
              )}
            />

            <FormField
              control={form.control}
              name='range'
              render={({ field }) => (
                <div className='mt-md'>
                  <BalanceSlider
                    title={t('transfer_range')}
                    {...field}
                    onChange={(value) => {
                      field.onChange(value)
                      const amount = +((value * availableAmount) / 100).toFixed(
                        0,
                      )
                      form.setValue('amount', amount)
                    }}
                    onCommit={(value) => {
                      const amount = +((value * availableAmount) / 100).toFixed(
                        0,
                      )
                      handleRangeAmountChange(amount)
                    }}
                    max={100}
                    currencyLabel={assetLabel}
                    color='neutral'
                    availableBalance={availableAmount.toString()}
                  />
                </div>
              )}
              disabled={!selectedAsset}
            />
            <FormField
              control={form.control}
              name='amount'
              render={({ field }) => (
                <FormItemWrapper
                  title={t('transfer_amount')}
                  caption={
                    <span className='flex items-center gap-2xs '>
                      {t('transfer_amount_caption')}{' '}
                      <PaymentType
                        size='xs'
                        numberSize='xs'
                        value={amountInIRR}
                      />
                    </span>
                  }
                  inputSize='md'
                >
                  <Col className='gap-4xl'>
                    <TransferAmountInput
                      {...field}
                      onChange={(value) => {
                        field.onChange(value)

                        // form.setValue('amount', value)
                      }}
                      onAmountChange={(value) => {
                        handleAmountChange(value)
                      }}
                      assetLabel={assetLabel}
                      disabled={!selectedAsset}
                    />
                  </Col>
                </FormItemWrapper>
              )}
              disabled={!selectedAsset}
            />

            <FormField
              control={form.control}
              name='transferTo'
              render={({ field }) => (
                <FormItemWrapper
                  title={t('transfer_transfer_to')}
                  inputSize='md'
                  caption={t('transfer_transfer_to_caption')}
                >
                  <Input
                    {...field}
                    placeholder={t('transfer_transfer_to_placeholder')}
                    inputSize='md'
                    className='w-full'
                  />
                </FormItemWrapper>
              )}
            />

            <ActionDetailsCondition>
              <Row className='justify-between mt-md mb-xs'>
                <Typography
                  size='xs'
                  weight='regular'
                  color='text-neutral-surface-80'
                >
                  {t('transfer_transfer_fee')}
                </Typography>

                <PaymentType
                  size='2xs'
                  numberColor='text-neutral-surface-80'
                  isBold={false}
                  value={fee ? +fee : 0}
                  className='min-h-6'
                />
              </Row>

              <TransferDialog
                feeCurrency={feeCurrency}
                amountInIRR={amountInIRR}
                amount={amount}
                assetTitle={selectedAsset?.name || ''}
                assetToken={selectedAsset?.token || ''}
                fee={fee ? +fee : 0}
                open={dialogOpen}
                transferTo={form.watch('transferTo')}
                onOpenChange={(open: boolean) => {
                  setDialogOpen(open)
                }}
                selectedAsset={selectedAsset}
              />

              <Button
                size='lg'
                color='base'
                className='w-full mt-lg'
                type='submit'
              >
                {t('transfer_continue')}
              </Button>
            </ActionDetailsCondition>
          </Col>
        </form>
      </Form>
    </Card>
  )
}
